const TRUE_VALUE = true;

export const selectCommercialControl = (state = {}) => state.commercialControl || {};

export const selectCommercialCapabilities = (state = {}) =>
    selectCommercialControl(state).capabilities || {};

export const getUsageByTypeFromSnapshot = (usageSnapshot = {}) => {
    if (!usageSnapshot || typeof usageSnapshot !== "object") return {};

    return usageSnapshot.usage ||
        usageSnapshot.usageByType ||
        usageSnapshot.data?.usage ||
        usageSnapshot.data?.usageByType ||
        {};
};

export const hasCommercialCapabilitiesLoaded = (capabilities = {}) =>
    !!capabilities && typeof capabilities === "object" && Object.keys(capabilities).length > 0;

export const hasCapability = (capabilities = {}, key) =>
    !!capabilities && Object.prototype.hasOwnProperty.call(capabilities, key);

export const isCapabilityEnabled = (capabilities = {}, key) =>
    capabilities?.[key] === TRUE_VALUE;

export const isFeatureLocked = (capabilities = {}, key) =>
    !isCapabilityEnabled(capabilities, key);

export const canUseStrategicMemo = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "strategic_memo_enabled") &&
    capabilities?.trial_strategic_memo_locked !== true;

export const canUseStandardExports = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "standard_exports_enabled") &&
    capabilities?.trial_exports_locked !== true;

export const canUseFullCommentDetails = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "full_comment_details_enabled");

export const getTrialFreeCommentDetailsLimit = (capabilities = {}) => {
    const value = capabilities?.trial_free_comment_details_limit;
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
};

export const canUseSupportingDocuments = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "supporting_documents_for_criteria");

export const canUseRiskComparison = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "risk_comparison_enabled");

export const canUseRerun = (capabilities = {}) => {
    const isPositiveOrCustom = (value) => {
        if (value && typeof value === "object") {
            return value.custom === true || value.unlimited === true || Number(value.limit) > 0;
        }
        return Number(value ?? 0) > 0;
    };

    return isPositiveOrCustom(capabilities?.max_reruns_per_criterion) &&
        isPositiveOrCustom(capabilities?.max_total_reruns_per_analysis);
};

export const isUsageAvailable = (usageByType = {}, usageType) => {
    const usage = usageByType?.[usageType];
    if (!usage || typeof usage !== "object") return false;
    if (usage.unlimited === true) return true;
    const remaining = Number(usage.remaining);
    return Number.isFinite(remaining) && remaining > 0;
};

export const canUseSupportingDocumentsWithUsage = (capabilities = {}, usageByType = {}) =>
    canUseSupportingDocuments(capabilities) &&
    isUsageAvailable(usageByType, "supporting_document");

export const canUseRiskComparisonWithUsage = (capabilities = {}, usageByType = {}) =>
    canUseRiskComparison(capabilities) &&
    isUsageAvailable(usageByType, "risk_comparison");

export const canUseRerunWithUsage = (capabilities = {}, usageByType = {}) =>
    canUseRerun(capabilities) &&
    isUsageAvailable(usageByType, "rerun");

export const canManageCriteria = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "criteria_management_enabled") ||
    isCapabilityEnabled(capabilities, "custom_criteria_enabled");

export const canEditCriteriaPrompts = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "criteria_prompt_editing_enabled");

export const canGenerateCriteriaPrompts = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "criteria_prompt_generation_enabled");

export const getCustomCriteriaSnapshot = (commercialControl = {}) =>
    commercialControl.customCriteria ||
    commercialControl.custom_criteria ||
    commercialControl.usageByType?.custom_criteria ||
    commercialControl.usageSnapshot?.custom_criteria ||
    commercialControl.usageSnapshot?.usage?.custom_criteria ||
    commercialControl.usageSnapshot?.usageByType?.custom_criteria ||
    commercialControl.usageSnapshot?.data?.custom_criteria ||
    commercialControl.usageSnapshot?.data?.usage?.custom_criteria ||
    commercialControl.usageSnapshot?.data?.usageByType?.custom_criteria ||
    {};

export const canCreateCustomCriteria = (capabilities = {}, customCriteria = {}) => {
    if (!canManageCriteria(capabilities)) return false;
    if (customCriteria?.can_create === false) return false;
    if (customCriteria?.unlimited === true) return true;
    const remaining = Number(customCriteria?.remaining);
    if (Number.isFinite(remaining)) return remaining > 0;
    const used = Number(customCriteria?.used);
    const limit = Number(customCriteria?.limit);
    if (Number.isFinite(used) && Number.isFinite(limit)) return used < limit;
    return true;
};

export const isCustomCriterion = (criterion = {}) =>
    !!criterion &&
    criterion.visibility_scope !== "SYSTEM" &&
    criterion.client_id !== null &&
    criterion.client_id !== undefined;

export const isCustomCriteriaLimitExceeded = (customCriteria = {}) => {
    if (customCriteria?.unlimited === true) return false;
    const used = Number(customCriteria?.used);
    const limit = Number(customCriteria?.limit);
    return Number.isFinite(used) && Number.isFinite(limit) && used > limit;
};

export const canMutateCustomCriterion = (
    criterion = {},
    capabilities = {},
    customCriteria = {},
    plan = {},
) => {
    if (!isCustomCriterion(criterion)) return true;
    if (!canManageCriteria(capabilities)) return false;
    if (String(plan?.code || "").toLowerCase() === "trial") return false;
    return !isCustomCriteriaLimitExceeded(customCriteria);
};

export const canViewSystemPrompts = (capabilities = {}, user = {}) =>
    isCapabilityEnabled(capabilities, "system_prompts_visible") &&
    String(user?.role || "").toUpperCase() === "SUPER_ADMIN";

export const canUseRepository = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "repository_enabled") ||
    isCapabilityEnabled(capabilities, "advanced_repository_enabled");

export const getMaxWorkspaceUsers = (capabilities = {}) => {
    if (!hasCapability(capabilities, "max_workspace_users")) return null;
    const value = capabilities?.max_workspace_users;
    if (value === null) return Infinity;
    if (value === undefined || value === "") return null;
    if (typeof value === "object") {
        if (value.custom === true || value.unlimited === true) return Infinity;
        const limit = Number(value.limit);
        return Number.isFinite(limit) ? limit : null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const canManageWorkspaceUsers = (capabilities = {}, user = {}) => {
    const role = String(user?.role || "").toUpperCase();
    if (role === "SUPER_ADMIN") return true;
    if (role !== "ADMIN_CLIENT") return false;
    const maxUsers = getMaxWorkspaceUsers(capabilities);
    return maxUsers === Infinity || (Number.isFinite(maxUsers) && maxUsers > 1);
};

export const canStartCompleteAnalysisFromUsage = (usageByType = {}) => {
    return isUsageAvailable(usageByType, "complete_analysis");
};

export const canUseAdvancedContractProfile = (capabilities = {}) =>
    isCapabilityEnabled(capabilities, "advanced_contract_profile_enabled") &&
    capabilities?.contract_profile_advanced_locked !== true &&
    String(capabilities?.contract_profile_visibility_level || "").toLowerCase() !== "basic";

export const isAdvancedContractProfileLocked = (capabilities = {}) =>
    !canUseAdvancedContractProfile(capabilities);

export const getMaxCriteriaPerAnalysis = (capabilities = {}) => {
    const value = capabilities?.max_criteria_per_analysis;
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getAllowedUploadFormats = (capabilities = {}) => {
    const formats = capabilities?.allowed_upload_formats;
    if (!Array.isArray(formats)) return null;
    return formats
        .map((format) => String(format || "").trim().replace(/^\./, "").toLowerCase())
        .filter(Boolean);
};

export const buildAcceptAttributeFromFormats = (formats = null) => {
    if (!Array.isArray(formats) || formats.length === 0) return null;
    return formats.map((format) => `.${String(format).replace(/^\./, "")}`).join(",");
};

const FEATURE_COPY = {
    strategic_memo: {
        titleFr: "Mémo stratégique verrouillé",
        bodyFr: "Le mémo stratégique est disponible avec un plan supérieur.",
        titleEn: "Strategic memo locked",
        bodyEn: "The strategic memo is available with a higher plan.",
    },
    standard_export: {
        titleFr: "Export verrouillé",
        bodyFr: "Les exports standards sont disponibles avec un plan supérieur.",
        titleEn: "Export locked",
        bodyEn: "Standard exports are available with a higher plan.",
    },
    comment_details: {
        titleFr: "Détails avancés verrouillés",
        bodyFr: "Les détails actionnables complets sont disponibles avec un plan supérieur.",
        titleEn: "Advanced details locked",
        bodyEn: "Full actionable details are available with a higher plan.",
    },
    supporting_document: {
        titleFr: "Document d'appui verrouillé",
        bodyFr: "Les documents d'appui par critère sont limités par le plan actif.",
        titleEn: "Supporting document locked",
        bodyEn: "Supporting documents per criterion are limited by the active plan.",
    },
    risk_comparison: {
        titleFr: "Comparaison verrouillée",
        bodyFr: "La comparaison orientée risque est limitée par le plan actif.",
        titleEn: "Comparison locked",
        bodyEn: "Risk-oriented comparison is limited by the active plan.",
    },
    criteria_management: {
        titleFr: "Gestion des critères verrouillée",
        bodyFr: "La création et la gestion avancée des critères sont disponibles avec un plan supérieur.",
        titleEn: "Criteria management locked",
        bodyEn: "Criteria creation and advanced management are available with a higher plan.",
    },
    custom_criteria_limit: {
        titleFr: "Limite de critères personnalisés atteinte",
        bodyFr: "Votre plan ne permet pas de créer davantage de critères personnalisés.",
        titleEn: "Custom criteria limit reached",
        bodyEn: "Your plan does not allow creating more custom criteria.",
    },
    criteria_prompt_editing: {
        titleFr: "Édition des prompts verrouillée",
        bodyFr: "L'édition des prompts de critères personnalisés dépend du plan actif.",
        titleEn: "Prompt editing locked",
        bodyEn: "Editing custom criterion prompts depends on the active plan.",
    },
    repository: {
        titleFr: "Référentiel verrouillé",
        bodyFr: "Le référentiel avancé est limité par le plan actif.",
        titleEn: "Repository locked",
        bodyEn: "The advanced repository is limited by the active plan.",
    },
    contract_profile_advanced: {
        titleFr: "Profil du contrat avancé verrouillé",
        bodyFr: "Les sections avancées du profil du contrat sont disponibles avec un plan supérieur.",
        titleEn: "Advanced contract profile locked",
        bodyEn: "Advanced contract profile sections are available with a higher plan.",
    },
    prompt_internal: {
        titleFr: "Prompts système masqués",
        bodyFr: "Les prompts système Verdictia ne sont visibles que dans une interface interne autorisée.",
        titleEn: "System prompts hidden",
        bodyEn: "Verdictia system prompts are only visible in an authorized internal interface.",
    },
    max_criteria: {
        titleFr: "Limite de critères atteinte",
        bodyFr: "Le nombre de critères sélectionnés dépasse la limite du plan actif.",
        titleEn: "Criteria limit reached",
        bodyEn: "The selected criteria count exceeds the active plan limit.",
    },
    complete_analysis: {
        titleFr: "Analyse indisponible",
        bodyFr: "Votre plan ne permet pas de lancer une nouvelle analyse pour une nouvelle version documentaire.",
        titleEn: "Analysis unavailable",
        bodyEn: "Your plan does not allow starting a new analysis for a new document version.",
    },
};

export const hasLockedFeatureCopy = (featureKey) =>
    !!featureKey && Object.prototype.hasOwnProperty.call(FEATURE_COPY, featureKey);

export const buildLockedCommercialDecision = (featureKey, overrides = {}) => {
    const copy = FEATURE_COPY[featureKey] || FEATURE_COPY.comment_details;
    const code = overrides.code || `feature_locked_${featureKey || "unknown"}`;
    return {
        allowed: false,
        status: "blocked",
        code,
        message: {
            code,
            title_key: overrides.titleKey || `commercial.locked.${featureKey}.title`,
            body_key: overrides.bodyKey || `commercial.locked.${featureKey}.body`,
            default_title_fr: overrides.titleFr || copy.titleFr,
            default_body_fr: overrides.bodyFr || copy.bodyFr,
            default_title_en: overrides.titleEn || copy.titleEn,
            default_body_en: overrides.bodyEn || copy.bodyEn,
            default_cta_label_fr: overrides.ctaFr || "Voir les options",
            default_cta_label_en: overrides.ctaEn || "View options",
            cta_target: overrides.ctaTarget || "upgrade",
            severity: "blocked",
            source: "frontend_capability_lock",
        },
        metadata_json: {
            feature: featureKey,
            ...(overrides.metadata_json || {}),
        },
    };
};
